import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, Card, GhostButton, DemoBadge } from "@/components/Primitives";
import StatusPill from "@/components/StatusPill";
import { ArrowLeft } from "@phosphor-icons/react";

function FieldRow({ label, value, mono }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="py-2 border-b border-white/5">
      <div className="text-zinc-500 text-[9px] tracking-widest uppercase mb-0.5">{label}</div>
      <div className={`text-[12px] text-zinc-300 leading-relaxed whitespace-pre-wrap ${mono ? "font-mono-data" : ""}`}>{String(value)}</div>
    </div>
  );
}

function Block({ title, children }) {
  return (
    <Card className="p-4">
      <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2">{title}</div>
      {children}
    </Card>
  );
}

export default function SearchRuleDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [rule, setRule] = useState(undefined);

  useEffect(() => {
    let active = true;
    api.get("/collections/search_rules").then((r) => {
      if (!active) return;
      setRule((r.data || []).find((x) => x.id === id) || null);
    }).catch(() => setRule(null));
    return () => { active = false; };
  }, [id]);

  if (rule === undefined) return <div className="p-8 text-zinc-500">Loading…</div>;
  if (rule === null) return <div className="p-8 text-zinc-500" data-testid="rule-not-found">Search rule not found. <button onClick={() => nav("/search-rules")} className="text-[#FFB300] underline">Back</button></div>;

  return (
    <div>
      <PageHeader
        overline="Discovery Configuration · Read-Only"
        title={<span>{rule.rule_name}<DemoBadge show={rule.is_demo} /></span>}
        sub={`${rule.product_category || "—"} · ${rule.division || "—"}`}
        actions={<GhostButton onClick={() => nav("/search-rules")} data-testid="rule-back-btn"><ArrowLeft size={12} className="inline mr-1" />Back to Rules</GhostButton>}
      />
      <div className="p-8 grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="search-rule-detail">
        <Block title="Overview">
          <div className="flex flex-wrap gap-2 mb-2">
            {rule.priority && <StatusPill value={rule.priority} />}
            <StatusPill value={rule.active ? "Active" : "Off"} />
            {rule.automation_readiness && <StatusPill value={rule.automation_readiness} />}
          </div>
          <FieldRow label="Regions" value={rule.countries_regions} />
          <FieldRow label="Buyer Types" value={rule.buyer_types} />
          <FieldRow label="Deadline Window (days)" value={rule.deadline_window_days} mono />
          <FieldRow label="Publication Window (days)" value={rule.publication_window_days} mono />
          <FieldRow label="Linked Search Intelligence" value={rule.linked_search_intelligence} />
        </Block>

        <Block title="Keywords">
          <FieldRow label="Inclusion Keywords" value={rule.inclusion_keywords} />
          <FieldRow label="Exact Phrases" value={rule.exact_phrases} />
          <FieldRow label="Native-Language Keywords" value={rule.native_language_keywords} />
          <FieldRow label="Exclusion Keywords" value={rule.exclusion_keywords} />
        </Block>

        <Block title="Codes — Candidate / To Verify">
          <FieldRow label="Candidate CPV / Category Codes (to verify)" value={rule.candidate_cpv_codes_to_verify} mono />
          <FieldRow label="CPV/NAICS Numeric (query)" value={rule.cpv_naics_category_codes} mono />
          <div className="text-[10px] text-zinc-600 italic mt-1.5">Codes are candidate values pending official verification, not confirmed mappings.</div>
        </Block>

        <Block title="Manual-Review & Risk Triggers">
          <FieldRow label="Manual-Download Warning Terms" value={rule.manual_download_warning_terms} />
          <FieldRow label="Eligibility / Origin Red Flags" value={rule.eligibility_origin_red_flags} />
          <FieldRow label="Sample / Testing Terms" value={rule.sample_testing_terms} />
          <FieldRow label="Bond / Guarantee Terms" value={rule.bond_guarantee_terms} />
          <FieldRow label="Local-Partner Warning Terms" value={rule.local_partner_warning_terms} />
          <FieldRow label="False-Positive Patterns" value={rule.false_positive_patterns} />
        </Block>

        <Block title="Strategy">
          <FieldRow label="TED Query Strategy" value={rule.ted_query_strategy} />
          <FieldRow label="Local Portal Strategy" value={rule.local_portal_strategy} />
        </Block>
      </div>
    </div>
  );
}
